import { useLayoutEffect, useRef } from 'react';
import Konva from 'konva';
import { useOrgChartViewStore } from '@/stores/orgchartViewStore';
import { useDiagramStore } from '@/stores/diagramStore';

interface UseAnimatedLayoutPositionOptions {
  enabled: boolean;
}

const MAX_TWEEN_NODES = 220;

/**
 * Animates a Konva Group's position when layout coordinates change.
 *
 * On each x/y change the hook snaps the Konva node back to the *previous*
 * position (before the browser paints) then tweens to the new position over
 * 300 ms.
 *
 * First mount is always instant (no "slide from nowhere"). A 400 ms grace
 * period after first mount also suppresses animation so that the initial
 * layout recalculation (~100 ms after expand) doesn't cause a visible slide.
 *
 * Returns a mutable ref that the consumer should set to `true` while
 * dragging to prevent the tween from fighting the user's drag.
 */
export function useAnimatedLayoutPosition(
  groupRef: React.RefObject<Konva.Group | null>,
  x: number,
  y: number,
  options: UseAnimatedLayoutPositionOptions,
): React.MutableRefObject<boolean> {
  const { enabled } = options;

  const prevPos = useRef<{ x: number; y: number } | null>(null);
  const mountedAt = useRef<number>(0);
  const tweenRef = useRef<Konva.Tween | null>(null);
  const suppressRef = useRef(false);

  useLayoutEffect(() => {
    const node = groupRef.current;
    if (!node) {
      prevPos.current = { x, y };
      return;
    }

    // First mount — record position, no animation
    if (prevPos.current === null) {
      prevPos.current = { x, y };
      mountedAt.current = performance.now();
      return;
    }

    const oldX = prevPos.current.x;
    const oldY = prevPos.current.y;
    prevPos.current = { x, y };

    // No movement
    if (oldX === x && oldY === y) return;

    // Disabled, dragging, or within the recently-entered grace period
    if (
      !enabled ||
      suppressRef.current ||
      performance.now() - mountedAt.current < 600
    ) {
      return;
    }

    // Only animate position changes triggered by collapse/expand (within 2s window).
    // Other layout changes (e.g. adding a new node) should not slide siblings around.
    const lastCollapse = useOrgChartViewStore.getState().lastCollapseAt;
    if (Date.now() - lastCollapse > 2000) return;
    if (useDiagramStore.getState().nodes.length > MAX_TWEEN_NODES) return;

    // Kill any running tween
    if (tweenRef.current) {
      tweenRef.current.destroy();
      tweenRef.current = null;
    }

    // Snap the Konva node back to old position (before paint)
    node.x(oldX);
    node.y(oldY);

    // Tween to new position
    const tween = new Konva.Tween({
      node,
      duration: 0.5,
      x,
      y,
      easing: Konva.Easings.EaseInOut,
      onFinish: () => {
        tweenRef.current = null;
      },
    });
    tweenRef.current = tween;
    tween.play();
  }, [x, y, enabled, groupRef]);

  // Cleanup on unmount
  useLayoutEffect(() => {
    return () => {
      if (tweenRef.current) {
        tweenRef.current.destroy();
        tweenRef.current = null;
      }
    };
  }, []);

  return suppressRef;
}
